import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  useWebsiteNcofEducatorFormConfig,
  useSaveWebsiteNcofEducatorFormConfig,
  DEFAULT_EDUCATOR_POSITION_OPTIONS,
  type WebsiteNcofEducatorFormConfigUpdate,
} from '../hooks/useWebsiteNcofEducatorFormConfig';
import {
  useWebsiteNcofEducatorSubmissions,
  useUpdateWebsiteNcofEducatorSubmissionReadStatus,
  useUpdateWebsiteNcofEducatorSubmission,
  type WebsiteNcofEducatorSubmission,
} from '../hooks/useWebsiteNcofEducatorSubmissions';

type AdminTab = 'submissions' | 'form_settings';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 1rem;
  font-size: 0.9rem;
`;

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 1.25rem;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active
        ? 'color-mix(in srgb, var(--primary) 45%, var(--outline))'
        : 'color-mix(in srgb, var(--outline) 80%, transparent)'};
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  background: ${({ $active }) =>
    $active
      ? 'color-mix(in srgb, var(--primary) 12%, var(--surface))'
      : 'color-mix(in srgb, var(--surface) 85%, var(--surfaceVariant))'};
  color: ${({ $active }) => ($active ? 'var(--onSurface)' : 'var(--onSurfaceVariant)')};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: min(700px, 100%);
  max-height: min(90vh, 900px);
  overflow: auto;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--outline);
  position: sticky;
  top: 0;
  background: var(--surface);
  z-index: 1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-size: 1.15rem;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  display: grid;
  gap: 0.8rem;
`;

const DetailGroup = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const DetailLabel = styled.label`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailValue = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const FieldInput = styled.input`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
  font: inherit;
  font-size: 0.92rem;
  box-sizing: border-box;
`;

const FieldSelect = styled.select`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
  font: inherit;
  font-size: 0.92rem;
  box-sizing: border-box;
`;

const FieldTextarea = styled.textarea`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
  font: inherit;
  font-size: 0.92rem;
  min-height: 5.5rem;
  resize: vertical;
  box-sizing: border-box;
`;

const StatusPill = styled.span<{ $isRead: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.15rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ $isRead }) => ($isRead ? 'var(--surfaceVariant)' : 'var(--primary)')};
  color: ${({ $isRead }) => ($isRead ? 'var(--onSurfaceVariant)' : 'var(--onPrimary)')};
`;

const StatusButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 9999px;
  padding: 0.45rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  border-radius: 9999px;
  padding: 0.55rem 1rem;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SettingsCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  padding: 1.25rem;
  display: grid;
  gap: 1rem;
  max-width: 760px;
`;

const SettingsGrid = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: var(--onSurface);
  font-size: 0.92rem;
  font-weight: 600;
`;

const FeedbackText = styled.p<{ $tone?: 'success' | 'error' }>`
  margin: 0;
  font-size: 0.88rem;
  color: ${({ $tone }) =>
    $tone === 'error' ? 'var(--error)' : $tone === 'success' ? 'var(--primary)' : 'var(--onSurfaceVariant)'};
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
`;

const emptyFormSettings = (): WebsiteNcofEducatorFormConfigUpdate => ({
  is_enabled: true,
  eyebrow: 'School Partnerships',
  hero_title: 'NCOF Educators',
  hero_intro:
    'For partners that work in the school system. Share your details so we can connect and support educators in our community.',
  info_title: 'Who this is for',
  info_bullets: [
    'Teachers, administrators, and school staff who partner with New Church of Faith.',
    'Educators interested in tutoring and other school-system partnerships.',
    'Anyone serving students through classroom, office, guidance, or support roles.',
  ],
  info_footer: 'Submissions go directly to the church team for follow-up.',
  form_title: 'Educator signup',
  form_intro: 'Tell us about your role in the school system.',
  tutoring_question: 'Would you be interested in tutoring?',
  success_message:
    'Thank you! Your NCOF Educators signup has been submitted. Our team will follow up soon.',
  position_options: [...DEFAULT_EDUCATOR_POSITION_OPTIONS],
});

export const NcofEducatorsAdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('submissions');
  const { data, isLoading, error } = useWebsiteNcofEducatorSubmissions(activeTab === 'submissions');
  const {
    data: formConfig,
    isLoading: formConfigLoading,
    error: formConfigError,
  } = useWebsiteNcofEducatorFormConfig(activeTab === 'form_settings' || activeTab === 'submissions');
  const saveFormConfig = useSaveWebsiteNcofEducatorFormConfig();
  const updateReadStatus = useUpdateWebsiteNcofEducatorSubmissionReadStatus();
  const updateSubmission = useUpdateWebsiteNcofEducatorSubmission();

  const [selectedSubmission, setSelectedSubmission] = useState<WebsiteNcofEducatorSubmission | null>(
    null
  );
  const [editDraft, setEditDraft] = useState<WebsiteNcofEducatorSubmission | null>(null);
  const [submissionFeedback, setSubmissionFeedback] = useState<string | null>(null);
  const [formSettings, setFormSettings] = useState<WebsiteNcofEducatorFormConfigUpdate>(
    emptyFormSettings()
  );
  const [formFeedback, setFormFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  const hasData = (data?.length ?? 0) > 0;
  const positionOptions = useMemo(() => {
    const fromConfig = formConfig?.position_options?.filter(Boolean) ?? [];
    if (fromConfig.length > 0) return fromConfig;
    return [...DEFAULT_EDUCATOR_POSITION_OPTIONS];
  }, [formConfig]);

  useEffect(() => {
    if (!formConfig) return;
    setFormSettings({
      is_enabled: formConfig.is_enabled,
      eyebrow: formConfig.eyebrow,
      hero_title: formConfig.hero_title,
      hero_intro: formConfig.hero_intro,
      info_title: formConfig.info_title,
      info_bullets: formConfig.info_bullets,
      info_footer: formConfig.info_footer,
      form_title: formConfig.form_title,
      form_intro: formConfig.form_intro,
      tutoring_question: formConfig.tutoring_question,
      success_message: formConfig.success_message,
      position_options: formConfig.position_options,
    });
  }, [formConfig]);

  const openSubmission = (submission: WebsiteNcofEducatorSubmission) => {
    setSelectedSubmission(submission);
    setEditDraft(submission);
    setSubmissionFeedback(null);
    if (!submission.is_read) {
      updateReadStatus.mutate({ id: submission.id, isRead: true });
      const next = { ...submission, is_read: true, read_at: new Date().toISOString() };
      setSelectedSubmission(next);
      setEditDraft(next);
    }
  };

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedSubmission(null);
        setEditDraft(null);
      }
    };

    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, []);

  const saveSubmissionEdits = async () => {
    if (!editDraft) return;
    setSubmissionFeedback(null);
    if (!editDraft.name.trim() || !editDraft.email.trim() || !editDraft.phone.trim()) {
      setSubmissionFeedback('Name, email, and phone are required.');
      return;
    }
    if (!editDraft.school.trim() || !editDraft.position.trim()) {
      setSubmissionFeedback('School and position are required.');
      return;
    }

    try {
      await updateSubmission.mutateAsync({
        id: editDraft.id,
        updates: {
          name: editDraft.name,
          email: editDraft.email,
          phone: editDraft.phone,
          school: editDraft.school,
          position: editDraft.position,
          grade: editDraft.grade,
          subject: editDraft.subject,
          interested_in_tutoring: editDraft.interested_in_tutoring,
        },
      });
      setSelectedSubmission(editDraft);
      setSubmissionFeedback('Saved.');
    } catch {
      setSubmissionFeedback('Could not save changes. Please try again.');
    }
  };

  const saveFormSettings = async () => {
    setFormFeedback(null);
    const positions = formSettings.position_options.map((item) => item.trim()).filter(Boolean);
    if (!formSettings.hero_title.trim() || !formSettings.form_title.trim()) {
      setFormFeedback({ type: 'error', text: 'Hero title and form title are required.' });
      return;
    }
    if (positions.length === 0) {
      setFormFeedback({ type: 'error', text: 'Add at least one position option.' });
      return;
    }

    try {
      await saveFormConfig.mutateAsync({
        ...formSettings,
        position_options: positions,
        info_bullets: formSettings.info_bullets.map((item) => item.trim()).filter(Boolean),
      });
      setFormFeedback({ type: 'success', text: 'Form settings saved. The public page will use these values.' });
    } catch {
      setFormFeedback({
        type: 'error',
        text: 'Could not save form settings. You may need to run the form config migration.',
      });
    }
  };

  return (
    <Page>
      <HeaderTitle>NCOF Educators</HeaderTitle>
      <HelperText>
        Review educator signups and update the public NCOF Educators form content and position
        options.
      </HelperText>

      <TabBar role="tablist" aria-label="NCOF Educators admin sections">
        <TabButton
          type="button"
          role="tab"
          $active={activeTab === 'submissions'}
          aria-selected={activeTab === 'submissions'}
          onClick={() => setActiveTab('submissions')}
        >
          Submissions
        </TabButton>
        <TabButton
          type="button"
          role="tab"
          $active={activeTab === 'form_settings'}
          aria-selected={activeTab === 'form_settings'}
          onClick={() => setActiveTab('form_settings')}
        >
          Form settings
        </TabButton>
      </TabBar>

      {activeTab === 'submissions' && (
        <>
          {isLoading && <HelperText>Loading…</HelperText>}
          {error && (
            <HelperText style={{ color: 'var(--error)' }}>
              Could not load educator submissions. You may need to run the migration for
              website_ncof_educator_submissions.
            </HelperText>
          )}

          {!isLoading && hasData && (
            <TableCard>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>School</th>
                      <th>Position</th>
                      <th>Tutoring</th>
                      <th>Status</th>
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.map((submission) => (
                      <ClickableRow
                        key={submission.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openSubmission(submission)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openSubmission(submission);
                          }
                        }}
                      >
                        <td>{submission.name ?? '—'}</td>
                        <td>{submission.email ?? '—'}</td>
                        <td>{submission.school ?? '—'}</td>
                        <td>{submission.position ?? '—'}</td>
                        <td>{submission.interested_in_tutoring ? 'Yes' : 'No'}</td>
                        <td>
                          <StatusPill $isRead={submission.is_read}>
                            {submission.is_read ? 'Read' : 'Unread'}
                          </StatusPill>
                        </td>
                        <td>
                          {submission.created_at
                            ? new Date(submission.created_at).toLocaleString()
                            : '—'}
                        </td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </TableCard>
          )}

          {!isLoading && !hasData && !error && (
            <HelperText>No NCOF Educators signups yet.</HelperText>
          )}
        </>
      )}

      {activeTab === 'form_settings' && (
        <>
          {formConfigLoading && <HelperText>Loading form settings…</HelperText>}
          {formConfigError && (
            <HelperText style={{ color: 'var(--error)' }}>
              Could not load form settings. You may need to run the migration for
              website_ncof_educator_form_config.
            </HelperText>
          )}
          {!formConfigLoading && !formConfigError && (
            <SettingsCard>
              <HelperText style={{ margin: 0 }}>
                Changes here update the public `/ncof-educators` page. Position options are one per
                line.
              </HelperText>
              <SettingsGrid>
                <CheckboxRow>
                  <input
                    type="checkbox"
                    checked={formSettings.is_enabled}
                    onChange={(event) =>
                      setFormSettings((prev) => ({ ...prev, is_enabled: event.target.checked }))
                    }
                  />
                  Form enabled on the public website
                </CheckboxRow>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-eyebrow">Eyebrow</DetailLabel>
                  <FieldInput
                    id="edu-eyebrow"
                    value={formSettings.eyebrow}
                    onChange={(event) =>
                      setFormSettings((prev) => ({ ...prev, eyebrow: event.target.value }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-hero-title">Hero title</DetailLabel>
                  <FieldInput
                    id="edu-hero-title"
                    value={formSettings.hero_title}
                    onChange={(event) =>
                      setFormSettings((prev) => ({ ...prev, hero_title: event.target.value }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-hero-intro">Hero intro</DetailLabel>
                  <FieldTextarea
                    id="edu-hero-intro"
                    value={formSettings.hero_intro}
                    onChange={(event) =>
                      setFormSettings((prev) => ({ ...prev, hero_intro: event.target.value }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-info-title">Info card title</DetailLabel>
                  <FieldInput
                    id="edu-info-title"
                    value={formSettings.info_title}
                    onChange={(event) =>
                      setFormSettings((prev) => ({ ...prev, info_title: event.target.value }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-info-bullets">Info bullets (one per line)</DetailLabel>
                  <FieldTextarea
                    id="edu-info-bullets"
                    value={formSettings.info_bullets.join('\n')}
                    onChange={(event) =>
                      setFormSettings((prev) => ({
                        ...prev,
                        info_bullets: event.target.value.split('\n'),
                      }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-info-footer">Info footer</DetailLabel>
                  <FieldInput
                    id="edu-info-footer"
                    value={formSettings.info_footer}
                    onChange={(event) =>
                      setFormSettings((prev) => ({ ...prev, info_footer: event.target.value }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-form-title">Form title</DetailLabel>
                  <FieldInput
                    id="edu-form-title"
                    value={formSettings.form_title}
                    onChange={(event) =>
                      setFormSettings((prev) => ({ ...prev, form_title: event.target.value }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-form-intro">Form intro</DetailLabel>
                  <FieldInput
                    id="edu-form-intro"
                    value={formSettings.form_intro}
                    onChange={(event) =>
                      setFormSettings((prev) => ({ ...prev, form_intro: event.target.value }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-tutoring-q">Tutoring question</DetailLabel>
                  <FieldInput
                    id="edu-tutoring-q"
                    value={formSettings.tutoring_question}
                    onChange={(event) =>
                      setFormSettings((prev) => ({
                        ...prev,
                        tutoring_question: event.target.value,
                      }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-success">Success message</DetailLabel>
                  <FieldTextarea
                    id="edu-success"
                    value={formSettings.success_message}
                    onChange={(event) =>
                      setFormSettings((prev) => ({
                        ...prev,
                        success_message: event.target.value,
                      }))
                    }
                  />
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel htmlFor="edu-positions">Position options (one per line)</DetailLabel>
                  <FieldTextarea
                    id="edu-positions"
                    value={formSettings.position_options.join('\n')}
                    onChange={(event) =>
                      setFormSettings((prev) => ({
                        ...prev,
                        position_options: event.target.value.split('\n'),
                      }))
                    }
                  />
                </DetailGroup>
              </SettingsGrid>
              <ActionRow>
                <PrimaryButton
                  type="button"
                  onClick={() => void saveFormSettings()}
                  disabled={saveFormConfig.isPending}
                >
                  {saveFormConfig.isPending ? 'Saving…' : 'Save form settings'}
                </PrimaryButton>
                {formFeedback && (
                  <FeedbackText $tone={formFeedback.type}>{formFeedback.text}</FeedbackText>
                )}
              </ActionRow>
            </SettingsCard>
          )}
        </>
      )}

      {selectedSubmission && editDraft && (
        <ModalOverlay
          onClick={() => {
            setSelectedSubmission(null);
            setEditDraft(null);
          }}
        >
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Educator Signup Details</ModalTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <StatusButton
                  type="button"
                  onClick={async () => {
                    const nextIsRead = !editDraft.is_read;
                    await updateReadStatus.mutateAsync({
                      id: editDraft.id,
                      isRead: nextIsRead,
                    });
                    const next = {
                      ...editDraft,
                      is_read: nextIsRead,
                      read_at: nextIsRead ? new Date().toISOString() : null,
                    };
                    setEditDraft(next);
                    setSelectedSubmission(next);
                  }}
                  disabled={updateReadStatus.isPending}
                >
                  {editDraft.is_read ? 'Mark as unread' : 'Mark as read'}
                </StatusButton>
                <ModalCloseButton
                  type="button"
                  aria-label="Close educator signup details"
                  onClick={() => {
                    setSelectedSubmission(null);
                    setEditDraft(null);
                  }}
                >
                  ×
                </ModalCloseButton>
              </div>
            </ModalHeader>
            <ModalBody>
              <DetailGroup>
                <DetailLabel htmlFor="sub-name">Name</DetailLabel>
                <FieldInput
                  id="sub-name"
                  value={editDraft.name}
                  onChange={(event) =>
                    setEditDraft((prev) => (prev ? { ...prev, name: event.target.value } : prev))
                  }
                />
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="sub-email">Email</DetailLabel>
                <FieldInput
                  id="sub-email"
                  type="email"
                  value={editDraft.email}
                  onChange={(event) =>
                    setEditDraft((prev) => (prev ? { ...prev, email: event.target.value } : prev))
                  }
                />
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="sub-phone">Phone</DetailLabel>
                <FieldInput
                  id="sub-phone"
                  value={editDraft.phone}
                  onChange={(event) =>
                    setEditDraft((prev) => (prev ? { ...prev, phone: event.target.value } : prev))
                  }
                />
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="sub-school">School</DetailLabel>
                <FieldInput
                  id="sub-school"
                  value={editDraft.school}
                  onChange={(event) =>
                    setEditDraft((prev) => (prev ? { ...prev, school: event.target.value } : prev))
                  }
                />
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="sub-position">Position</DetailLabel>
                <FieldSelect
                  id="sub-position"
                  value={editDraft.position}
                  onChange={(event) =>
                    setEditDraft((prev) =>
                      prev ? { ...prev, position: event.target.value } : prev
                    )
                  }
                >
                  {!positionOptions.includes(editDraft.position) && editDraft.position ? (
                    <option value={editDraft.position}>{editDraft.position}</option>
                  ) : null}
                  {positionOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </FieldSelect>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="sub-grade">Grade</DetailLabel>
                <FieldInput
                  id="sub-grade"
                  value={editDraft.grade ?? ''}
                  onChange={(event) =>
                    setEditDraft((prev) => (prev ? { ...prev, grade: event.target.value } : prev))
                  }
                />
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="sub-subject">Subject</DetailLabel>
                <FieldInput
                  id="sub-subject"
                  value={editDraft.subject ?? ''}
                  onChange={(event) =>
                    setEditDraft((prev) =>
                      prev ? { ...prev, subject: event.target.value } : prev
                    )
                  }
                />
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="sub-tutoring">Interested in tutoring</DetailLabel>
                <FieldSelect
                  id="sub-tutoring"
                  value={editDraft.interested_in_tutoring ? 'yes' : 'no'}
                  onChange={(event) =>
                    setEditDraft((prev) =>
                      prev
                        ? { ...prev, interested_in_tutoring: event.target.value === 'yes' }
                        : prev
                    )
                  }
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </FieldSelect>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Submitted</DetailLabel>
                <DetailValue>
                  {editDraft.created_at ? new Date(editDraft.created_at).toLocaleString() : '—'}
                </DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Status</DetailLabel>
                <DetailValue>{editDraft.is_read ? 'Read' : 'Unread'}</DetailValue>
              </DetailGroup>
              <ActionRow>
                <PrimaryButton
                  type="button"
                  onClick={() => void saveSubmissionEdits()}
                  disabled={updateSubmission.isPending}
                >
                  {updateSubmission.isPending ? 'Saving…' : 'Save changes'}
                </PrimaryButton>
                {submissionFeedback && (
                  <FeedbackText
                    $tone={submissionFeedback === 'Saved.' ? 'success' : 'error'}
                  >
                    {submissionFeedback}
                  </FeedbackText>
                )}
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};
